import { useMemo, useRef, useState } from 'react';
import type {
  MedicationSearchResultT,
  SearchMedicationsParams,
} from '@lotushealth/lotushealth_js_sdk/schemas';
import { z } from 'zod';

import { useQueryClient } from '@tanstack/react-query';

import {
  selectMedicationQueryDefinition,
  useSearchMedicationsQuery,
  type RxMedication,
} from '@/entities/rx';

const medicationSearchTextSchema = z
  .string()
  .trim()
  .min(3, 'Type at least 3 characters to search medications.');

export interface MedicationSearchRow {
  dispensableDrugId: number;
  label: string;
  strength: string | null;
  isObsolete: boolean;
  ndc: string | null;
}

interface MedicationSearchResolved {
  row: MedicationSearchRow;
  selected: RxMedication;
  selectedLabel: string;
  dispenseUnitId: number | null;
}

interface UseMedicationSearchPickerOptions {
  initialSelectedLabel?: string | null;
  onResolve: (resolved: MedicationSearchResolved) => void;
}

export interface UseMedicationSearchPickerResult {
  isOpen: boolean;
  setIsOpen: (open: boolean) => void;
  searchText: string;
  setSearchText: (text: string) => void;
  isSearchTextValid: boolean;
  searchTextError: string | null;
  searchQuery: ReturnType<typeof useSearchMedicationsQuery>;
  results: MedicationSearchRow[];
  isSelecting: boolean;
  selectError: string | null;
  selectMedication: (row: MedicationSearchRow) => Promise<void>;
  selectedLabel: string | null;
}

function toMedicationSearchRow(result: MedicationSearchResultT): MedicationSearchRow | null {
  const id = result.DispensableDrugId;
  if (id === null || id === undefined || !Number.isFinite(id) || id <= 0) {
    return null;
  }

  return {
    dispensableDrugId: id,
    label: result.NameWithRouteDoseForm?.trim() || `Medication ${id}`,
    strength: result.Strength?.trim() || null,
    isObsolete: result.IsObsolete === true,
    ndc: result.NDC?.trim() || null,
  };
}

export function useMedicationSearchPicker({
  initialSelectedLabel = null,
  onResolve,
}: UseMedicationSearchPickerOptions): UseMedicationSearchPickerResult {
  const queryClient = useQueryClient();
  const [isOpen, setIsOpen] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [isSelecting, setIsSelecting] = useState(false);
  const [selectError, setSelectError] = useState<string | null>(null);
  const [selectedLabel, setSelectedLabel] = useState<string | null>(initialSelectedLabel);
  const lastSelectedIdRef = useRef<number | null>(null);

  const trimmedSearchText = searchText.trim();
  const searchValidation = useMemo(
    () => medicationSearchTextSchema.safeParse(trimmedSearchText),
    [trimmedSearchText]
  );
  const isSearchTextValid = searchValidation.success;
  const searchTextError = searchValidation.success
    ? null
    : (searchValidation.error.issues[0]?.message ?? null);

  const searchParams = useMemo((): SearchMedicationsParams | undefined => {
    if (!isSearchTextValid) {
      return undefined;
    }
    return { name: trimmedSearchText, page_number: 1, drug_status: null };
  }, [isSearchTextValid, trimmedSearchText]);

  const searchQuery = useSearchMedicationsQuery(searchParams);

  const results = useMemo((): MedicationSearchRow[] => {
    return (searchQuery.data ?? [])
      .map((result) => toMedicationSearchRow(result))
      .filter((row): row is MedicationSearchRow => row !== null);
  }, [searchQuery.data]);

  const selectMedication = async (row: MedicationSearchRow) => {
    setSelectError(null);
    setIsSelecting(true);
    setIsOpen(false);

    try {
      lastSelectedIdRef.current = row.dispensableDrugId;
      const selected = await queryClient.fetchQuery(
        selectMedicationQueryDefinition({ dispensable_drug_id: row.dispensableDrugId })
      );

      const nextLabel =
        selected.DisplayName?.trim() ||
        selected.DrugName?.trim() ||
        row.label ||
        `Medication ${row.dispensableDrugId}`;
      const dispenseUnitId =
        selected.DispenseUnitId != null &&
        Number.isFinite(selected.DispenseUnitId) &&
        selected.DispenseUnitId > 0
          ? selected.DispenseUnitId
          : null;

      setSelectedLabel(nextLabel);
      onResolve({ row, selected, selectedLabel: nextLabel, dispenseUnitId });
    } catch {
      const lastId = lastSelectedIdRef.current;
      if (lastId !== null) {
        setSelectedLabel((prev) => prev ?? `Medication ${lastId}`);
      }
      setSelectError('Could not load medication details. Please try again.');
    } finally {
      setIsSelecting(false);
    }
  };

  return {
    isOpen,
    setIsOpen,
    searchText,
    setSearchText,
    isSearchTextValid,
    searchTextError,
    searchQuery,
    results,
    isSelecting,
    selectError,
    selectMedication,
    selectedLabel,
  };
}
